import React from 'react';

// Support both snake_case (API) and camelCase formats
interface FeedbackItem {
  feedbackId?: string;
  senderName?: string;
  senderInitials?: string;
  senderFname?: string;
  senderLname?: string;
  relationshipType?: string;
  feedbackDate?: string;
  feedbackText?: string;
  sourceType?: string;
  tagSource?: string;
  // snake_case (from API)
  feedback_id?: string;
  sender_name?: string;
  sender_initials?: string;
  sender_fname?: string;
  sender_lname?: string;
  relationship_type?: string;
  feedback_date?: string;
  feedback_text?: string;
  source_type?: string;
  tag_source?: string;
}

// Helpers for snake_case/camelCase compatibility
const getFeedbackId = (f: FeedbackItem) => f.feedbackId || f.feedback_id || '';
const getSenderName = (f: FeedbackItem) => f.senderName || f.sender_name || '';
const getSenderInitials = (f: FeedbackItem) => {
  if (f.senderInitials || f.sender_initials) {
    return f.senderInitials || f.sender_initials || '';
  }
  const fname = f.senderFname || f.sender_fname || '';
  const lname = f.senderLname || f.sender_lname || '';
  return `${fname.charAt(0)}${lname.charAt(0)}`.toUpperCase();
};
const getRelationshipType = (f: FeedbackItem) => f.relationshipType || f.relationship_type || 'Peer';
const getFeedbackDate = (f: FeedbackItem) => f.feedbackDate || f.feedback_date || '';
const getFeedbackText = (f: FeedbackItem) => f.feedbackText || f.feedback_text || '';
const getTagSource = (f: FeedbackItem) => f.tagSource || f.tag_source || '';

interface FeedbacksReceivedWidgetProps {
  feedbacks?: FeedbackItem[] | { data: FeedbackItem[] };
  loading?: boolean;
  error?: string;
  options?: {
    showAvatar?: boolean;
    showTags?: boolean;
    showDate?: boolean;
    maxCards?: number;
  };
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getRelationshipStyles = (relationshipType: string) => {
  const type = relationshipType.toLowerCase();
  switch (type) {
    case 'peer':
      return {
        cardBg: 'bg-emerald-50', cardBorder: 'border-emerald-100',
        avatarBg: 'bg-emerald-200', avatarText: 'text-emerald-700',
        tagBg: 'bg-emerald-100', tagText: 'text-emerald-700',
      };
    case 'manager':
      return {
        cardBg: 'bg-blue-50', cardBorder: 'border-blue-100',
        avatarBg: 'bg-blue-200', avatarText: 'text-blue-700',
        tagBg: 'bg-blue-100', tagText: 'text-blue-700',
      };
    case 'direct report':
      return {
        cardBg: 'bg-purple-50', cardBorder: 'border-purple-100',
        avatarBg: 'bg-purple-200', avatarText: 'text-purple-700',
        tagBg: 'bg-purple-100', tagText: 'text-purple-700',
      };
    case 'check-in':
      return {
        cardBg: 'bg-amber-50', cardBorder: 'border-amber-100',
        avatarBg: 'bg-amber-200', avatarText: 'text-amber-700',
        tagBg: 'bg-amber-100', tagText: 'text-amber-700',
      };
    default:
      return {
        cardBg: 'bg-gray-50', cardBorder: 'border-gray-100',
        avatarBg: 'bg-gray-200', avatarText: 'text-gray-700',
        tagBg: 'bg-gray-100', tagText: 'text-gray-600',
      };
  }
};

const extractTags = (tagSource: string): string[] => {
  if (!tagSource) return [];
  const tagKeywords: Record<string, string> = {
    'leadership': 'Leadership', 'collaboration': 'Collaboration', 'technical': 'Technical',
    'communication': 'Communication', 'mentoring': 'Mentoring', 'coaching': 'Coaching',
    'innovation': 'Innovation', 'teamwork': 'Teamwork', 'problem solving': 'Problem Solving',
  };
  const lowerSource = tagSource.toLowerCase();
  const foundTags: string[] = [];
  for (const [keyword, displayTag] of Object.entries(tagKeywords)) {
    if (lowerSource.includes(keyword) && !foundTags.includes(displayTag)) {
      foundTags.push(displayTag);
    }
  }
  if (foundTags.length === 0 && tagSource.length > 0) {
    foundTags.push(tagSource.length > 15 ? tagSource.substring(0, 15) + '...' : tagSource);
  }
  return foundTags.slice(0, 2);
};

const stripHtml = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'").replace(/\s+/g, ' ').trim();
};

// Extract plain text from BlockNote JSON content
const extractBlockNoteText = (value: string): string => {
  try {
    const blocks = JSON.parse(value);
    if (!Array.isArray(blocks)) return value;
    return blocks
      .map((block: any) =>
        (block.content || [])
          .filter((c: any) => c.type === 'text')
          .map((c: any) => c.text || '')
          .join('')
      )
      .filter(Boolean)
      .join(' ');
  } catch {
    return value;
  }
};

const cleanText = (text: string): string => {
  if (!text) return '';
  const extracted = extractBlockNoteText(text);
  return stripHtml(extracted);
};

const truncateText = (text: string, maxLength: number): string => {
  const cleaned = cleanText(text);
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength).trim() + '...';
};

export const FeedbacksReceivedWidget: React.FC<FeedbacksReceivedWidgetProps> = ({
  feedbacks,
  loading,
  error,
  options = {}
}) => {
  const { showAvatar = true, showTags = true, showDate = true, maxCards = 20 } = options;

  // Handle wrapped data format
  const feedbacksList: FeedbackItem[] = Array.isArray(feedbacks)
    ? feedbacks
    : ((feedbacks as { data: FeedbackItem[] })?.data || []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return <div className="flex items-center justify-center h-full py-8 text-red-500 text-sm">{error}</div>;
  }

  const relationshipOrder: Record<string, number> = {
    'manager': 1,
    'peer': 2,
    'check-in': 3,
  };

  const sortedFeedbacks = [...feedbacksList].sort((a, b) => {
    const aType = getRelationshipType(a).toLowerCase();
    const bType = getRelationshipType(b).toLowerCase();
    return (relationshipOrder[aType] ?? 4) - (relationshipOrder[bType] ?? 4);
  });

  const displayFeedbacks = sortedFeedbacks.slice(0, maxCards);

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-sm">Feedbacks Received</h3>
        <span className="text-xs text-gray-400">Last 90 days</span>
      </div>

      {/* Feedback Cards - Horizontal Scroll */}
      <div className="flex-1 min-h-0 flex gap-4 overflow-x-auto pb-2">
        {displayFeedbacks.length === 0 ? (
          <div className="flex items-center justify-center w-full py-6 text-gray-500 text-sm">
            No feedback received yet
          </div>
        ) : (
          displayFeedbacks.map((feedback) => {
            const relationshipType = getRelationshipType(feedback);
            const tags = extractTags(getTagSource(feedback));
            const styles = getRelationshipStyles(relationshipType);

            return (
              <div
                key={getFeedbackId(feedback)}
                className={`p-4 ${styles.cardBg} border ${styles.cardBorder} rounded-xl flex-shrink-0`}
                style={{ width: '320px' }}
              >
                {/* Sender Info */}
                <div className="flex items-center gap-3 mb-3">
                  {showAvatar && (
                    <div className={`w-10 h-10 rounded-full ${styles.avatarBg} flex items-center justify-center ${styles.avatarText} text-sm font-semibold`}>
                      {getSenderInitials(feedback)}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-900">{getSenderName(feedback)}</div>
                    <div className="text-xs text-gray-400">
                      {relationshipType}
                      {showDate && getFeedbackDate(feedback) && ` · ${formatDate(getFeedbackDate(feedback))}`}
                    </div>
                  </div>
                </div>

                {/* Feedback Text */}
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  "{truncateText(getFeedbackText(feedback), 180)}"
                </p>

                {/* Tags */}
                {showTags && tags.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    {tags.map((tag, idx) => (
                      <span key={idx} className={`text-[11px] ${styles.tagBg} ${styles.tagText} px-2 py-0.5 rounded`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FeedbacksReceivedWidget;
